import PortalIdParser from 'PortalIdParser';
import { useCallback, useMemo } from 'react';
import { Field, Form, useForm } from 'react-final-form';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Form as FoundationsForm } from 'foundations-components/transitional/components/Form';
import { H2 } from 'foundations-components/transitional/components/H2';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { View } from 'foundations-components/transitional/components/View';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import ErrorBoundary from 'academy-common-lib/components/Errors/ErrorBoundary';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import { FinalFormField } from 'academy-common-lib/components/FinalForm';
import { ALL_CONTENT_PATH } from '../../myTeam/constants/routes';
import { fromDateToUiDateInputFormat, fromUiDateInputFormatToUnixTs } from 'academy-common-lib/utils/CanvasComponents';
import { required } from 'academy-common-lib/utils/validation';
import { useAccounts } from '../../accounts/accountsQueries';
import { HasScope } from '../../accounts/components/HasScope';
import { useCreateContentAssignment, useUpdateContentAssignment, useContentAssignmentsByIds } from '../contentAssignmentsQueries';
import { HubSpotterAccountUsersSelect } from '../../assignments/components/HubSpotterAccountUsersSelect';
import { TeamsSelect } from '../../assignments/components/TeamsSelect';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ContentAssignedNotification = ({
  assigneeCount,
  assignmentType,
  contentId,
  contentName,
  contentType
}) => {
  const contentTypeLabel = I18n.text(`academyCommon.contentTypes.${contentType}`).toLowerCase();
  return /*#__PURE__*/_jsxs(ButtonWrapper, {
    children: [assignmentType === 'teams' ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.contentAssignments.assignmentsCreated.team",
      options: {
        count: assigneeCount,
        contentName,
        contentType: contentTypeLabel
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.contentAssignments.assignmentsCreated.teamMember",
      options: {
        count: assigneeCount,
        contentName,
        contentType: contentTypeLabel
      }
    }), /*#__PURE__*/_jsx(ACRouterButton, {
      marginInlineStart: "400",
      size: "small",
      to: {
        pathname: ALL_CONTENT_PATH,
        search: `contentIds=${contentId}`
      },
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyCommon.contentAssignments.viewAssignments"
      })
    })]
  });
};
const AccountsTabs = ({
  myAccounts,
  allAccounts
}) => {
  const form = useForm();
  const formState = form.getState();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Field, {
      name: "accountsRadio",
      render: ({
        input
      }) => /*#__PURE__*/_jsxs(ToggleGroup, {
        name: "accountsRadio",
        paddingBlockStart: "300",
        children: [/*#__PURE__*/_jsx(RadioInput, Object.assign({}, input, {
          defaultChecked: true,
          inline: true,
          value: "myAccounts",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.myAccounts"
          })
        })), /*#__PURE__*/_jsxs(RadioInput, Object.assign({}, input, {
          inline: true,
          value: "allAccounts",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.allAccounts"
          }), /*#__PURE__*/_jsx(Tooltip, {
            placement: "bottom",
            title: I18n.text('academyCommon.playlists.allAccountsTooltip'),
            children: /*#__PURE__*/_jsx(View, {
              display: "inline-block",
              marginInlineStart: "100",
              children: /*#__PURE__*/_jsx(InfoIcon, {
                size: "100"
              })
            })
          })]
        }))]
      })
    }), formState.values.accountsRadio === 'allAccounts' ? allAccounts : formState.values.accountsRadio === 'myAccounts' ? myAccounts : null]
  });
};
const ServerError = () => {
  return /*#__PURE__*/_jsxs(Alert, {
    marginBlockEnd: "300",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.errors.badRequest.somethingsGoneWrong"
    }),
    type: "danger",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.errors.badRequest.refreshPage"
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.errors.badRequest.keepsHappening"
    })]
  });
};
const AssigneeSelector = ({
  input,
  accountId,
  isEditing,
  selectedUsers
}) => {
  const form = useForm();
  const formState = form.getState();
  const handleAssignmentTypeChange = () => {
    form.change('assignees', []);
  };
  const hasAssignees = formState.values.assignees && formState.values.assignees.length > 0;
  const currentAssignmentType = formState.values.assignmentType;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Field, {
      name: "assignmentType",
      render: ({
        input: radioInput
      }) => /*#__PURE__*/_jsxs(ToggleGroup, {
        columnGap: "200",
        name: "assignmentType",
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          disabled: isEditing || !hasAssignees || currentAssignmentType === 'teamMembers',
          placement: "top",
          title: I18n.text('academyCommon.playlists.teamMembersTooltip'),
          children: /*#__PURE__*/_jsx(RadioInput, Object.assign({}, radioInput, {
            defaultChecked: true,
            disabled: hasAssignees && currentAssignmentType !== 'teamMembers' || isEditing,
            inline: true,
            onChange: e => {
              radioInput.onChange(e);
              handleAssignmentTypeChange();
            },
            value: "teamMembers",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.playlists.assignTeamMembers"
            })
          }))
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: isEditing || !hasAssignees || currentAssignmentType === 'teams',
          placement: "top",
          title: I18n.text('academyCommon.playlists.teamsTooltip'),
          children: /*#__PURE__*/_jsx(RadioInput, Object.assign({}, radioInput, {
            disabled: hasAssignees && currentAssignmentType !== 'teams' || isEditing,
            inline: true,
            onChange: e => {
              radioInput.onChange(e);
              handleAssignmentTypeChange();
            },
            value: "teams",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.playlists.assignTeams"
            })
          }))
        })]
      })
    }), formState.values.assignmentType === 'teamMembers' ? /*#__PURE__*/_jsx(HubSpotterAccountUsersSelect, Object.assign({}, input, {
      accountId: accountId,
      disabled: isEditing || !formState.values.assignmentType,
      selectedUsers: selectedUsers
    })) : /*#__PURE__*/_jsx(TeamsSelect, Object.assign({}, input, {
      accountId: accountId,
      disabled: isEditing || !formState.values.assignmentType
    }))]
  });
};
const HubSpotterPortalUserAssign = ({
  isMultiUserAccount,
  isEditing,
  accountId,
  accounts,
  selectedUsers
}) => {
  const form = useForm();
  const handleAccountChange = () => {
    form.change('assignees', []);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FinalFormField, {
      fieldProps: {
        name: 'account'
      },
      formControlProps: {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.assignAccount"
        }),
        marginBlockStart: '700'
      },
      hidden: !isMultiUserAccount,
      render: input => /*#__PURE__*/_jsx(TextInput, Object.assign({}, input, {
        disabled: accounts.length === 1 || isEditing,
        inputMode: "numeric",
        onChange: e => {
          input.onChange(e);
          handleAccountChange();
        },
        type: "text"
      })),
      validators: [required]
    }), /*#__PURE__*/_jsx(FinalFormField, {
      fieldProps: {
        name: 'assignees'
      },
      formControlProps: {
        marginBlockStart: '700',
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.assignAssignee"
        })
      },
      render: input => /*#__PURE__*/_jsx(AssigneeSelector, {
        accountId: accountId,
        input: input,
        isEditing: isEditing,
        selectedUsers: selectedUsers
      }),
      validators: [required]
    })]
  });
};
const StandardPortalUserAssign = ({
  isMultiUserAccount,
  isEditing,
  accountId,
  accounts,
  selectedUsers
}) => {
  const form = useForm();
  const handleAccountChange = () => {
    form.change('assignees', []);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FinalFormField, {
      fieldProps: {
        name: 'account'
      },
      formControlProps: {
        marginBlockStart: '700',
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.assignAccount"
        })
      },
      hidden: !isMultiUserAccount,
      render: input => /*#__PURE__*/_jsx(Select, Object.assign({}, input, {
        disabled: accounts.length === 1 || isEditing,
        onChange: e => {
          input.onChange(e);
          handleAccountChange();
        },
        options: accounts.map(account => ({
          value: account.id,
          text: I18n.text('academyCommon.playlists.assignAccountNameAndId', {
            accountName: I18n.SafeString(account.accountName),
            accountId: account.id.toString()
          })
        }))
      })),
      validators: [required]
    }), /*#__PURE__*/_jsx(FinalFormField, {
      fieldProps: {
        name: 'assignees'
      },
      formControlProps: {
        marginBlockStart: '700',
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.assignAssignee"
        })
      },
      render: input => /*#__PURE__*/_jsx(AssigneeSelector, {
        accountId: accountId,
        input: input,
        isEditing: isEditing,
        selectedUsers: selectedUsers
      }),
      validators: [required]
    })]
  });
};
const ContentAssignForm = ({
  accounts,
  hasError,
  dueDate,
  handleSubmitAsync,
  selectedUsers,
  isEditing,
  contentAssignments,
  portalId,
  onClose,
  loadedNavMarker
}) => {
  const assignmentIdNotFound = isEditing && contentAssignments && !contentAssignments.length;
  if (assignmentIdNotFound) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [loadedNavMarker, /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(Alert, {
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.contentAssignments.assignmentNotFound"
          }),
          type: "danger"
        })
      }), /*#__PURE__*/_jsx(DialogFooter, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.common.cancel"
          })
        })
      })]
    });
  }
  const isMultiUserAccount = (accounts || []).length > 1;
  return /*#__PURE__*/_jsx(Form, {
    initialValues: isEditing && contentAssignments ? {
      assignees: contentAssignments.map(ca => ca.assignee.userId),
      dueDate: dueDate ? fromDateToUiDateInputFormat(new Date(dueDate)) : null,
      accountsRadio: 'myAccounts',
      account: portalId,
      assignmentType: 'teamMembers'
    } : {
      dueDate: null,
      account: portalId || accounts.length && accounts[0].id || null,
      accountsRadio: 'myAccounts',
      assignmentType: 'teamMembers'
    },
    keepDirtyOnReinitialize: true,
    onSubmit: handleSubmitAsync,
    render: ({
      handleSubmit,
      values: {
        account: accountId,
        accountsRadio
      }
    }) => /*#__PURE__*/_jsxs(_Fragment, {
      children: [loadedNavMarker, /*#__PURE__*/_jsxs(FoundationsForm, {
        onSubmit: e => void handleSubmit(e),
        children: [/*#__PURE__*/_jsxs(DialogBody, {
          paddingBlockStart: "500",
          paddingInlineEnd: "900",
          paddingInlineStart: "800",
          children: [hasError && /*#__PURE__*/_jsx(ServerError, {}), /*#__PURE__*/_jsx("p", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: accountsRadio === 'allAccounts' ? 'academyCommon.contentAssignments.assignIntroductionPortalSearch' : isMultiUserAccount ? 'academyCommon.contentAssignments.assignIntroductionMultiAccount' : 'academyCommon.contentAssignments.assignIntroductionSingleAccount'
            })
          }), /*#__PURE__*/_jsx(HasScope, {
            fallback: /*#__PURE__*/_jsx(StandardPortalUserAssign, {
              accountId: accountId,
              accounts: accounts,
              isEditing: isEditing,
              isMultiUserAccount: isMultiUserAccount,
              selectedUsers: selectedUsers
            }),
            override: isEditing,
            scope: "academy-playlist-global-assignment",
            children: /*#__PURE__*/_jsx(AccountsTabs, {
              allAccounts: /*#__PURE__*/_jsx(HubSpotterPortalUserAssign, {
                accountId: accountId,
                accounts: accounts,
                isEditing: isEditing,
                isMultiUserAccount: isMultiUserAccount,
                selectedUsers: selectedUsers
              }),
              myAccounts: /*#__PURE__*/_jsx(StandardPortalUserAssign, {
                accountId: accountId,
                accounts: accounts,
                isEditing: isEditing,
                isMultiUserAccount: isMultiUserAccount,
                selectedUsers: selectedUsers
              })
            })
          }), /*#__PURE__*/_jsx(FinalFormField, {
            fieldProps: {
              name: 'dueDate',
              allowNull: true
            },
            formControlProps: {
              marginBlockStart: '700',
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academyCommon.common.dueDate"
              })
            },
            render: input => /*#__PURE__*/_jsx(DateInput, Object.assign({}, input, {
              min: fromDateToUiDateInputFormat(new Date()),
              tz: "utc"
            }))
          })]
        }), /*#__PURE__*/_jsxs(DialogFooter, {
          children: [/*#__PURE__*/_jsx(Button, {
            type: "submit",
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.common.assign"
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: onClose,
            use: "secondary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.common.cancel"
            })
          })]
        })]
      })]
    })
  });
};
const ContentAssignPanelInternal = ({
  contentId,
  contentType,
  contentName,
  portalId,
  assignmentIds,
  onClose,
  onSuccess,
  loadedNavMarker
}) => {
  const targetPortalId = portalId || PortalIdParser.getPortalIdFromPath();
  const isEditing = assignmentIds && !!assignmentIds.length;
  const {
    accounts
  } = useAccounts();
  const {
    contentAssignments
  } = useContentAssignmentsByIds({
    assignmentIds,
    skip: !isEditing
  });
  const selectedUsers = useMemo(() => (contentAssignments !== null && contentAssignments !== void 0 ? contentAssignments : []).map(assignment => {
    var _assignment$assignee$, _assignment$assignee$2, _assignment$assignee$3;
    return {
      id: assignment.assignee.userId,
      firstName: (_assignment$assignee$ = assignment.assignee.firstName) !== null && _assignment$assignee$ !== void 0 ? _assignment$assignee$ : '',
      lastName: (_assignment$assignee$2 = assignment.assignee.lastName) !== null && _assignment$assignee$2 !== void 0 ? _assignment$assignee$2 : '',
      email: (_assignment$assignee$3 = assignment.assignee.email) !== null && _assignment$assignee$3 !== void 0 ? _assignment$assignee$3 : ''
    };
  }), [contentAssignments]);
  const {
    createContentAssignment,
    createContentAssignmentError
  } = useCreateContentAssignment();
  const {
    updateContentAssignment,
    updateContentAssignmentError
  } = useUpdateContentAssignment();
  const handleSubmitAsync = useCallback(async formData => {
    const {
      dueDate,
      account,
      assignees,
      assignmentType
    } = formData;
    if (!assignees) {
      return;
    }
    const dueDateUnixTs = dueDate ? fromUiDateInputFormatToUnixTs(dueDate) : undefined;
    if (!isEditing) {
      UsageTracker.trackStandalone('Content Interaction', {
        action: 'content-assigned',
        contentId,
        contentType,
        portalId: targetPortalId,
        bulk: assignees.length > 1,
        count: assignees.length,
        date: dueDateUnixTs,
        assignmentType
      });
    }
    const response = isEditing ? await updateContentAssignment({
      assignmentIds,
      dueDate: dueDateUnixTs
    }) : await createContentAssignment(Object.assign({
      contentId,
      contentType: contentType,
      targetPortalId: account,
      dueDate: dueDateUnixTs
    }, assignmentType === 'teamMembers' ? {
      assigneeUserIds: assignees.map(Number)
    } : {
      teamIds: assignees.map(Number)
    })).then(createdAssignments => {
      if (createdAssignments) {
        FloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.contentAssignments.contentAssigned"
          }),
          message: /*#__PURE__*/_jsx(ContentAssignedNotification, {
            assigneeCount: assignees.length,
            assignmentType: assignmentType,
            contentId: contentId,
            contentName: contentName,
            contentType: contentType
          }),
          type: 'success'
        });
      }
      return createdAssignments;
    }).catch(err => {
      Raven.captureException(err);
    });
    if (response) {
      onSuccess === null || onSuccess === void 0 || onSuccess();
    }
  }, [assignmentIds, createContentAssignment, isEditing, onSuccess, contentId, contentType, contentName, targetPortalId, updateContentAssignment]);
  const dueDates = contentAssignments === null || contentAssignments === void 0 ? void 0 : contentAssignments.map(({
    dueDate
  }) => dueDate);
  const maybeCommonDueDate = dueDates && new Set(dueDates).size === 1 ? dueDates[0] : undefined;
  return /*#__PURE__*/_jsxs(ModalPanel, {
    width: 512,
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        justify: "between",
        children: [/*#__PURE__*/_jsx(H2, {
          marginBlockEnd: "0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.contentAssignments.assignContentName",
            options: {
              contentName,
              contentType: I18n.text(`academyCommon.contentTypes.${contentType}`).toLowerCase()
            }
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        })]
      })
    }), accounts && /*#__PURE__*/_jsx(ContentAssignForm, {
      accounts: accounts,
      contentAssignments: contentAssignments,
      dueDate: maybeCommonDueDate,
      handleSubmitAsync: handleSubmitAsync,
      hasError: !!(createContentAssignmentError || updateContentAssignmentError),
      isEditing: isEditing,
      loadedNavMarker: loadedNavMarker,
      onClose: onClose,
      portalId: targetPortalId,
      selectedUsers: selectedUsers
    })]
  });
};
export const ContentAssignPanel = props => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    children: /*#__PURE__*/_jsx(ContentAssignPanelInternal, Object.assign({}, props))
  });
};